import React from 'react';
import { Lock } from 'lucide-react';
import { usePlan } from '../../contexts/PlanContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { FEATURE_MOCKS } from './LockedFeatureMocks';

/**
 * <LockedFeature feature="analytics">
 *   <RealAnalyticsPanel />
 * </LockedFeature>
 *
 * If the user's plan unlocks the feature → renders children.
 * Otherwise → children are not mounted at all (no wasted API calls for a
 * feature the user can't use yet); instead shows a static, generic preview
 * of a populated dashboard — always looks full/attractive regardless of
 * whether the user's real data is empty — blurred behind an upgrade message.
 * The message sits directly on the blurred backdrop (no floating card/shadow):
 * UpgradeModal is the one real popup in this flow, this is just an inline notice.
 * Optional props let callers customize the locked-state copy.
 */
export const FEATURE_COPY = {
  analytics: {
    title: 'Analytics avanzado',
    description:
      'Mide el rendimiento de tu perfil: visitas, clics por red social, conversiones y tendencias en tiempo real.',
  },
  leads: {
    title: 'Gestión de leads',
    description:
      'Captura y organiza los contactos interesados en tu perfil. Filtra, etiqueta y exporta tu pipeline.',
  },
  teams: {
    title: 'Gestión de equipos',
    description:
      'Comparte el acceso a tus perfiles con tu equipo. Asigna roles, controla permisos y revisa actividad.',
    requiredPlanLabel: 'Enterprise',
  },
  team_analytics: {
    title: 'Analítica de equipo',
    description:
      'Mide el rendimiento combinado de todos los perfiles de tu equipo: visitas, clics, leads y ranking por miembro.',
    requiredPlanLabel: 'Enterprise',
  },
  brand_kit: {
    title: 'Marca de la empresa',
    description:
      'Define una vez el logo, la plantilla y los colores de tu empresa, y aplícalos a todos los perfiles de tu equipo.',
    requiredPlanLabel: 'Enterprise',
  },
  insights: {
    title: 'Insights con IA',
    description:
      'Recibe recomendaciones inteligentes basadas en el comportamiento real de los visitantes a tu perfil.',
  },
  exports: {
    title: 'Exportación ejecutiva',
    description:
      'Descarga reportes en Excel/PDF listos para compartir con tu equipo o presentar a clientes.',
  },
  multiple_profiles: {
    title: 'Múltiples perfiles',
    description:
      'Gestiona varios perfiles desde una sola cuenta — ideal para equipos, marcas o servicios múltiples.',
  },
  hub: {
    title: 'Hub de contactos',
    description:
      'Centraliza todos los contactos que captures (manualmente, leads, escaneo de tarjetas) en un solo lugar.',
  },
  google_sheets: {
    title: 'Integraciones',
    description:
      'Automatiza el envío de nuevos contactos y mantén la información de tu equipo sincronizada con Google Sheets.',
  },
  campaigns: {
    title: 'Eventos',
    description:
      'Agrupa los leads que captures en una feria o evento y mide el retorno: cuántos contactos, cuántos calificaron, cuántos se ganaron.',
  },
  reminders: {
    title: 'Recordatorios automáticos',
    description:
      'Automatiza el seguimiento a tus leads con una secuencia de correos programada, sin tener que acordarte tú.',
  },
  remove_watermark: {
    title: 'Marca de agua Contact',
    description:
      'Quita el logo de Contact de tu perfil público para una imagen 100% tuya, sin marca de terceros.',
  },
  developer_api: {
    title: 'API developer y webhooks',
    description:
      'Conecta Contact con tus propios sistemas: crea llaves de API y recibe webhooks en tiempo real.',
  },
  background_image: {
    title: 'Imagen de fondo personalizada',
    description:
      'Sube una imagen propia como fondo de tu perfil, en vez de usar solo un color.',
  },
  products: {
    title: 'Catálogo de productos',
    description:
      'Muestra tus productos o servicios directamente en tu perfil digital.',
  },
  quotations: {
    title: 'Cotizaciones',
    description:
      'Recibe y gestiona solicitudes de cotización directamente desde tu perfil.',
  },
};

const featureCopyTitle = (feature, t) => {
  const copy = FEATURE_COPY[feature];
  return copy ? t(`lockedFeature.${feature}.title`, copy.title) : undefined;
};
const featureCopyDescription = (feature, t) => {
  const copy = FEATURE_COPY[feature];
  return copy ? t(`lockedFeature.${feature}.description`, copy.description) : undefined;
};

// Fallback for any feature without a dedicated mock in LockedFeatureMocks.js —
// a neutral "populated dashboard" shape. Not real data (children never mount
// while locked), just a static, colorful stand-in so the blurred backdrop
// always looks full, regardless of whether the user's actual account has any
// data yet.
const MOCK_COLORS = ['#0071e3', '#34c759', '#7c3aed', '#ff9500'];

const GenericPreviewMock = () => (
  <div
    aria-hidden="true"
    style={{ filter: 'blur(6px) saturate(1.3)', pointerEvents: 'none', userSelect: 'none', padding: 24 }}
  >
    <div style={{ display: 'flex', gap: 12, marginBottom: 18, flexWrap: 'wrap' }}>
      {MOCK_COLORS.map((color, i) => (
        <div
          key={i}
          style={{
            flex: 1,
            minWidth: 90,
            background: '#fff',
            border: '1px solid rgba(0,0,0,0.07)',
            borderRadius: 16,
            padding: 14,
            boxShadow: '0 2px 10px rgba(0,0,0,0.04)',
          }}
        >
          <div style={{ width: 10, height: 10, borderRadius: '50%', background: color, marginBottom: 10 }} />
          <div style={{ width: '70%', height: 7, borderRadius: 4, background: '#d8d8dc', marginBottom: 10 }} />
          <div style={{ width: '50%', height: 18, borderRadius: 6, background: '#1d1d1f' }} />
        </div>
      ))}
    </div>
    {[0, 1, 2, 3].map((i) => (
      <div
        key={i}
        style={{
          background: '#fff',
          border: '1px solid rgba(0,0,0,0.07)',
          borderRadius: 14,
          padding: '12px 14px',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          marginBottom: 9,
          boxShadow: '0 2px 10px rgba(0,0,0,0.03)',
        }}
      >
        <div style={{ width: 32, height: 32, borderRadius: '50%', flexShrink: 0, background: MOCK_COLORS[i % 4] }} />
        <div style={{ flex: 1 }}>
          <div style={{ width: '50%', height: 9, borderRadius: 4, background: '#d8d8dc', marginBottom: 7 }} />
          <div style={{ width: '30%', height: 7, borderRadius: 4, background: '#e8e8ea' }} />
        </div>
        <div style={{ width: 58, height: 20, borderRadius: 980, flexShrink: 0, background: MOCK_COLORS[(i + 1) % 4] }} />
      </div>
    ))}
  </div>
);

const LockedFeature = ({ feature, children, title, description }) => {
  const { hasFeature, openUpgrade, loading } = usePlan();
  const { t } = useLanguage();

  if (loading) {
    // Render children silently while we don't know — backend still enforces gating
    return children || null;
  }

  if (hasFeature(feature)) {
    return children || null;
  }

  const copy = FEATURE_COPY[feature] || {};
  const planLabel = copy.requiredPlanLabel || 'Pro';
  const isEnterprise = planLabel === 'Enterprise';
  const accent = isEnterprise ? '#7c3aed' : '#0071e3';
  const tint = isEnterprise ? 'rgba(124,58,237,0.1)' : 'rgba(0,113,227,0.1)';
  const lockTitle = title || featureCopyTitle(feature, t) || t('lockedFeature.genericTitle', `Función ${planLabel}`).replace('{plan}', planLabel);
  const lockDesc = description || featureCopyDescription(feature, t) || t('lockedFeature.genericDescription', `Actualiza a ${planLabel} para desbloquear esta función.`).replace('{plan}', planLabel);
  const PreviewMock = FEATURE_MOCKS[feature] || GenericPreviewMock;

  return (
    <div
      className="relative w-full"
      data-testid={`locked-feature-${feature}`}
      style={{ minHeight: 380, borderRadius: 20, overflow: 'hidden', border: '1px solid rgba(0,0,0,0.07)' }}
    >
      <PreviewMock />

      <div
        className="absolute inset-0 flex items-center justify-center p-7"
        style={{
          background:
            'radial-gradient(ellipse 480px 420px at center, rgba(255,255,255,0.94) 0%, rgba(255,255,255,0.85) 35%, rgba(255,255,255,0.45) 65%, rgba(255,255,255,0.12) 100%)',
          zIndex: 10,
        }}
      >
        <div className="text-center" style={{ maxWidth: 320 }}>
          <div
            className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wider mb-4"
            style={{ background: tint, color: accent }}
          >
            <Lock size={11} strokeWidth={2.5} />
            {t('lockedFeature.planLabel', `Plan ${planLabel}`).replace('{plan}', planLabel)}
          </div>

          <h3 className="text-lg font-semibold mb-2" style={{ color: '#1d1d1f', letterSpacing: '-0.01em' }}>
            {lockTitle}
          </h3>
          <p className="text-sm leading-relaxed mb-5" style={{ color: '#6e6e73' }}>
            {lockDesc}
          </p>

          <button
            data-testid={`upgrade-cta-${feature}`}
            onClick={() => openUpgrade(feature)}
            className="w-full px-6 py-3 rounded-full font-medium text-white transition-all hover:opacity-90"
            style={{ background: accent }}
          >
            {t('lockedFeature.upgradeTo', `Actualizar a ${planLabel}`).replace('{plan}', planLabel)}
          </button>
          <p className="text-xs mt-3" style={{ color: '#a1a1a6' }}>
            {isEnterprise ? t('lockedFeature.contactSales', 'Contacta a ventas') : t('lockedFeature.fromPrice', 'Desde 2.500 CLP / mes')}
          </p>
        </div>
      </div>
    </div>
  );
};

export default LockedFeature;
